/* Explicit, confirmed whole-cart removal. Never retry a destructive request. */
(() => {
  'use strict';
  if (customElements.get('mini-cart-clear')) return;
  class MiniCartClear extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.hidden = false;
      this.opener = this.querySelector('[data-clear-open]');
      this.panel = this.querySelector('[data-clear-panel]');
      this.cancel = this.querySelector('[data-clear-cancel]');
      this.confirm = this.querySelector('[data-clear-confirm]');
      this.status = this.querySelector('[data-clear-status]');
      this.addEventListener('click', event => {
        if (event.target.closest('[data-clear-open]')) this.show();
        if (event.target.closest('[data-clear-cancel]')) this.hide();
        if (event.target.closest('[data-clear-confirm]')) this.clear();
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && !this.panel.hidden && !this.busy) {
          event.preventDefault(); event.stopPropagation(); this.hide();
        }
      });
      this.onClose = () => { if (!this.busy) this.hide(false); };
      this.drawer = this.closest('mini-cart');
      this.drawer?.addEventListener('mini-cart:closed', this.onClose);
    }
    disconnectedCallback() { this.drawer?.removeEventListener('mini-cart:closed', this.onClose); }
    show() {
      if (this.busy) return;
      this.panel.hidden = false;
      this.opener.hidden = true;
      this.cancel.focus();
    }
    hide(focus = true) {
      if (this.busy) return;
      this.panel.hidden = true;
      this.opener.hidden = false;
      if (focus) this.opener.focus();
    }
    async request(path, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch((this.dataset.root || '/').replace(/\/?$/, '/') + path, {
          credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal
        });
        if (!response.ok) throw new Error('Cart request failed');
        return await response.json();
      } finally { clearTimeout(timer); }
    }
    async clear() {
      if (this.busy || this.panel.hidden || this.confirm.disabled) return;
      const drawer = this.closest('mini-cart');
      if (!drawer || drawer.querySelector('.mini-cart-wrap.loading')) {
        this.status.textContent = 'Wait for your cart to finish updating, then try again.';
        return;
      }
      this.busy = true;
      this.confirm.disabled = true;
      this.cancel.disabled = true;
      this.status.textContent = 'Clearing your cart…';
      const form = drawer.querySelector('.mini-cart-inner');
      if (form) form.inert = true;
      drawer.setAttribute('aria-busy', 'true');
      let result;
      try {
        result = await this.request('cart/clear.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ sections: drawer.getSectionsToRender().map(s => s.section), sections_url: location.pathname })
        });
        if (result.item_count !== 0) throw new Error('Cart is not empty');
      } catch (_) {
        // A lost response may still have cleared the cart. Inspect, but never repeat the POST.
        try { result = await this.request('cart.js'); } catch (_) { result = null; }
        if (result?.item_count !== 0) {
          this.status.textContent = 'We could not confirm that your cart was cleared. Open View cart to check it before trying again.';
          this.cancel.disabled = false;
          this.busy = false;
          if (form) form.inert = false;
          drawer.removeAttribute('aria-busy');
          this.cancel.focus();
          return;
        }
      }
      try {
        if (result.sections && drawer.getSectionsToRender().every(s => result.sections[s.section])) drawer.renderContents(result);
        else await drawer.updateSectionContents();
        drawer.currentItemCount = 0;
        document.dispatchEvent(new CustomEvent('cart:cleared', { detail: { cart: result } }));
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart-clear', cartData: result });
        }
        if (form) form.inert = false;
        if (document.querySelector('cart-items')) location.reload();
        else drawer.querySelector('.mini-cart-message-empty a, .mini-cart-header button')?.focus();
      } catch (_) { location.reload(); }
      finally {
        if (form) form.inert = false;
        drawer.removeAttribute('aria-busy');
        this.busy = false;
      }
    }
  }
  customElements.define('mini-cart-clear', MiniCartClear);
})();
